import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchPeopleStuff } from '../../utils/services/fetchThing'
import DropdownSelect from '../shared/DropdownSelect' // Import du composant DropdownSelect
import RangeSlider from './RangeSlider'
import selectAllSubCategories from '../../utils/services/selectAllSubCategories'

const Filter = ({
    sellOrBuySection,
    userInfo,
    setFilteredResults,
    stuffCategories,
    currentPage,
    setCurrentPage,
    showFilter,
}) => {
    const [filters, setFilters] = useState({
        sellOrBuySection: sellOrBuySection,
        userId: userInfo?._id,
        userLonLat: userInfo?.coordinates,
        maxDistance: sellOrBuySection === 'buy' ? 500 : 99999, // km
        category: [],
        priceRange: '',
        searchTerm: '',
        expirationDate: '', // Ajout d'un filtre de date
    })
    const [selectedOptions, setSelectedOptions] = useState([])

    const resultsPerPage = 2
    const queryClient = useQueryClient()

    const { mutate: fetchFilteredResults } = useMutation({
        mutationFn: fetchPeopleStuff,
        onSuccess: (data) => {
            queryClient.setQueryData(
                ['peopleStuff', filters, currentPage],
                data,
                { staleTime: 5 * 60 * 1000 }
            )
            setFilteredResults(data)
        },
        onError: (err) =>
            console.error('Erreur lors de la récupération des données :', err),
    })

    useEffect(() => {
        fetchFilteredResults({ filters, page: currentPage, resultsPerPage })
    }, [currentPage]) // Dépendances : `filters`, `currentPage`
    useEffect(() => {
        setCurrentPage(1)
    }, [filters])

    const handleFilterChange = (name, value) => {
        if (name === 'expirationDate') {
            // Si expirationDate est sélectionné, mettez à jour les filtres
            setFilters((prevFilters) => ({
                ...prevFilters,
                expirationDate: value, // ici, value est une chaîne au format 'YYYY-MM-DD'
            }))
        } else if (name === 'category') {
            if (value === 'all') {
                setSelectedOptions(['all'])
                setFilters((prevFilters) => ({
                    ...prevFilters,
                    category: ['all'],
                }))
            } else {
                // set selected options
                setSelectedOptions(
                    selectedOptions.filter((category) =>
                        value.includes(category)
                    ).length > 0
                        ? selectedOptions.filter((cat) => cat !== value)
                        : selectedOptions
                              .concat(value)
                              .filter((cat) => cat !== 'all')
                )
                // Set all sub categories
                const allSubCategories = selectAllSubCategories(
                    stuffCategories,
                    value
                )
                const updatedCategories =
                    filters.category.filter((category) =>
                        allSubCategories.includes(category)
                    ).length > 0
                        ? filters.category.filter(
                              (cat) => !allSubCategories.includes(cat)
                          )
                        : filters.category
                              .concat(allSubCategories)
                              .filter((cat) => cat !== 'all')
                setFilters((prevFilters) => ({
                    ...prevFilters,
                    category: updatedCategories,
                }))
            }
        } else {
            setFilters((prevFilters) => ({
                ...prevFilters,
                [name]: value,
            }))
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        // Assurez-vous de convertir expirationDate en objet Date si nécessaire
        const updatedFilters = {
            ...filters,
            expirationDate: filters.expirationDate
                ? new Date(filters.expirationDate).toISOString()
                : null,
        }

        fetchFilteredResults({
            filters: updatedFilters,
            page: currentPage,
            resultsPerPage,
        })
    }

    if (userInfo || sellOrBuySection === 'buy') {
        if (showFilter) {
            return (
                <FormContainer onSubmit={handleSubmit}>
                    <SearchInput
                        type="text"
                        name="searchTerm"
                        placeholder="Recherche..."
                        value={filters.searchTerm}
                        onChange={(e) =>
                            handleFilterChange(e.target.name, e.target.value)
                        }
                    />

                    <DropdownSelect
                        name="category"
                        options={stuffCategories}
                        selectedOptions={selectedOptions}
                        onOptionClick={(value) =>
                            handleFilterChange('category', value)
                        }
                        placeholder="Sélectionnez une catégorie"
                    />

                    <RangeSlider min={0} max={1000} setFilters={setFilters} />

                    {sellOrBuySection === 'buy' && userInfo && (
                        <div>
                            <MaxDistanceInput
                                type="number"
                                name="maxDistance"
                                value={filters.maxDistance}
                                min="0"
                                max="9999"
                                onChange={(e) =>
                                    handleFilterChange(
                                        e.target.name,
                                        e.target.value
                                    )
                                }
                            />{' '}
                            km
                        </div>
                    )}

                    {/* Nouveau champ de filtre pour la date de péremption */}
                    <DateInput
                        type="date"
                        name="expirationDate"
                        value={filters.expirationDate}
                        onChange={(e) =>
                            handleFilterChange(e.target.name, e.target.value)
                        }
                    />

                    <SubmitButton type="submit">Filtrer</SubmitButton>
                </FormContainer>
            )
        }
    }
}

// Styled Components
const FormContainer = styled.form`
    position: absolute;
    top: 75px;
    background-color: white;
    display: flex;
    flex-direction: column;
    z-index: 500;
    padding: 10px;
    border-radius: 10px;
    box-shadow: 0 5px 15px;
    gap: 10px;
    width: 100%;
`

const SearchInput = styled.input`
    padding: 8px;
    font-size: 16px;
    border-radius: 4px;
    border: 1px solid #ddd;
`

const MaxDistanceInput = styled.input`
    padding: 8px;
    font-size: 16px;
    border-radius: 4px;
    border: 1px solid #ddd;
`

const DateInput = styled.input`
    padding: 8px;
    font-size: 16px;
    border-radius: 4px;
    border: 1px solid #ddd;
`

const SubmitButton = styled.button`
    padding: 10px 20px;
    background-color: #007bff;
    color: white;
    font-size: 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: #0056b3;
    }
`

export default Filter
